/**
 * Utilitaire de formatage des dates et heures pour AGEI.
 *
 * Principe : toutes les dates affichées utilisent le fuseau horaire du
 * navigateur de l'utilisateur (Intl.DateTimeFormat.resolvedOptions().timeZone)
 * afin d'être cohérentes peu importe le lieu de connexion.
 *
 * Les dates stockées en base sont au format ISO UTC (YYYY-MM-DDTHH:mm:ss.sssZ).
 * Elles sont converties vers l'heure locale au moment de l'affichage uniquement.
 */

const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone

const DEFAULT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: BROWSER_TZ,
}

const DEFAULT_DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: BROWSER_TZ,
}

const DEFAULT_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: BROWSER_TZ,
}

/**
 * Formate une date selon le fuseau horaire du navigateur.
 * @param value Date ISO ou Date
 * @param options Options Intl.DateTimeFormat (par défaut: JJ/MM/AAAA)
 * @returns Chaîne formatée ou '-' si invalide
 */
export function formatDate(
  value: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  return new Intl.DateTimeFormat('fr-FR', { timeZone: BROWSER_TZ, ...(options ?? DEFAULT_DATE_OPTIONS) }).format(d)
}

/**
 * Formate une date + heure selon le fuseau horaire du navigateur.
 * @param value Date ISO ou Date
 * @param options Options Intl.DateTimeFormat (par défaut: JJ/MM/AAAA HH:MM)
 * @returns Chaîne formatée ou '-' si invalide
 */
export function formatDateTime(
  value: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  return new Intl.DateTimeFormat('fr-FR', { timeZone: BROWSER_TZ, ...(options ?? DEFAULT_DATE_TIME_OPTIONS) }).format(d)
}

/**
 * Formate une heure selon le fuseau horaire du navigateur.
 * @param value Date ISO ou Date
 * @param options Options Intl.DateTimeFormat (par défaut: HH:MM)
 * @returns Chaîne formatée ou '-' si invalide
 */
export function formatTime(
  value: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  return new Intl.DateTimeFormat('fr-FR', { timeZone: BROWSER_TZ, ...(options ?? DEFAULT_TIME_OPTIONS) }).format(d)
}

/**
 * Formate une date « pure » (jour calendaire sans heure : date de facture,
 * échéance, jour de feuille de temps, congé…) SANS conversion de fuseau.
 *
 * Une chaîne `YYYY-MM-DD` (ou un ISO `YYYY-MM-DDT00:00:00.000Z` issu d'une
 * colonne date) est lue par ses seuls composants année/mois/jour, puis
 * formatée en UTC : le jour affiché ne change jamais, quel que soit le fuseau
 * du navigateur. Un objet `Date` est lu via ses composants locaux.
 * Ne PAS utiliser pour un horodatage réel (pointage, createdAt) : utiliser
 * formatDate / formatDateTime.
 * @param value Chaîne date-only/ISO ou Date
 * @param options Options Intl.DateTimeFormat (par défaut: JJ/MM/AAAA)
 * @returns Chaîne formatée ou '-' si invalide
 */
export function formatDateOnly(
  value: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!value) return '-'
  let utc: Date
  if (typeof value === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
    if (!m) return '-'
    utc = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  } else {
    utc = new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()))
  }
  if (Number.isNaN(utc.getTime())) return '-'
  return new Intl.DateTimeFormat('fr-FR', { ...(options ?? DEFAULT_DATE_OPTIONS), timeZone: 'UTC' }).format(utc)
}

/**
 * Retourne le fuseau horaire détecté du navigateur (ex: "Indian/Antananarivo").
 */
export function getBrowserTimezone(): string {
  return BROWSER_TZ
}

/**
 * Convertit une date locale en chaîne ISO UTC pour l'envoi à l'API.
 * Utile pour les champs de type date (input type="date").
 */
export function toLocalISO(value: string | Date): string {
  const d = new Date(value)
  return d.toISOString()
}
